/**
 * Maps backend RuleResponse objects to frontend BusinessRule objects.
 *
 * Bridges two vocabulary gaps:
 * 1. Department:  backend "accounting_finance" → frontend "AF"
 * 2. Rule type:   backend operational "range"  → frontend spec "threshold"
 *
 * These maps mirror the Python constants in
 *   askwingo_shared.config.constants (DEPARTMENT_PREFIXES, RULE_TYPE_TO_SPEC_TYPE).
 */

import type {
  BusinessRule,
  RuleSeverityLevel,
  RuleSource,
  RuleState,
  RuleType,
  RuleValidation,
  ValidationConflict,
  ValidationStatus,
} from '@/types/rule';

// ---------------------------------------------------------------------------
// Backend → Frontend vocabulary maps
// ---------------------------------------------------------------------------

/** Backend department slug → 2-letter frontend code. */
const DEPT_TO_CODE: Record<string, string> = {
  accounting_finance: 'AF',
  business_development: 'BD',
  maintenance: 'MN',
  production: 'PR',
  legal: 'LG',
  quality_assurance: 'QA',
  warehouse_shipping: 'WH',
  materials_management: 'MM',
  human_resources: 'HR',
};

/** Backend operational rule_type → frontend SpecRuleType.
 * All 11 canonical types are identity-mapped. Legacy aliases preserved. */
const RULE_TYPE_TO_SPEC: Record<string, RuleType> = {
  // Canonical spec types (identity)
  threshold: 'threshold',
  state_validation: 'state_validation',
  deviation: 'deviation',
  approval_gate: 'approval_gate',
  ccp: 'ccp',
  scheduled: 'scheduled',
  regex: 'regex',
  required: 'required',
  state_transition: 'state_transition',
  aggregate: 'aggregate',
  relational: 'relational',
  // Legacy aliases for backward compat with older DB rows
  range: 'threshold',
  percentage_change: 'deviation',
};

// ---------------------------------------------------------------------------
// Threshold display builder
// ---------------------------------------------------------------------------

/** Operator symbols for human-readable display. */
const OP_SYMBOLS: Record<string, string> = {
  gt: '>', gte: '≥', lt: '<', lte: '≤', eq: '=', neq: '≠', between: '↔',
};

/**
 * Build a human-readable threshold description from `rule_definition` JSON.
 * Covers all 11 spec rule types + legacy aliases.
 */
function buildThreshold(ruleType: string, def: Record<string, unknown>): string {
  if (!def || typeof def !== 'object') return '—';

  switch (ruleType) {
    // Legacy alias
    case 'range':
      return 'min' in def && 'max' in def ? `${fmt(def.min)} – ${fmt(def.max)}` : '—';

    case 'threshold':
      // New spec format: { operator, value, unit? }
      if ('operator' in def && 'value' in def) {
        const op = OP_SYMBOLS[String(def.operator)] ?? String(def.operator);
        const unit = def.unit ? ` ${def.unit}` : '';
        return `${op} ${fmt(def.value)}${unit}`;
      }
      // Legacy format: { threshold, direction }
      if ('threshold' in def) {
        const dir = def.direction === 'below' ? '<' : '>';
        return `${dir} ${fmt(def.threshold)}`;
      }
      return '—';

    case 'state_validation':
      if ('allowed_values' in def && Array.isArray(def.allowed_values))
        return `Allowed: ${(def.allowed_values as string[]).join(', ')}`;
      if ('allowed_transitions' in def && Array.isArray(def.allowed_transitions))
        return `${(def.allowed_transitions as string[][]).length} transitions`;
      return '—';

    case 'deviation':
    case 'percentage_change':
      return 'threshold_pct' in def
        ? `± ${fmt(def.threshold_pct)}%`
        : 'threshold' in def ? `± ${fmt(def.threshold)}%` : '—';

    case 'approval_gate':
      return 'threshold_value' in def
        ? `> ${fmt(def.threshold_value)} → ${def.required_role ?? 'approval'}`
        : '—';

    case 'ccp': {
      const op = OP_SYMBOLS[String(def.operator)] ?? String(def.operator);
      return `${op} ${fmt(def.value)} ${def.measurement_unit ?? ''}`.trim();
    }

    case 'scheduled':
      return `${def.trigger_condition ?? 'check'}: ${def.trigger_field ?? '—'}`;

    case 'regex':
      return 'pattern' in def ? `Pattern: ${String(def.pattern)}` : '—';

    case 'required':
      return def.custom_message ? String(def.custom_message) : 'Required field';

    case 'state_transition':
      return 'allowed_transitions' in def && Array.isArray(def.allowed_transitions)
        ? `${(def.allowed_transitions as string[][]).length}-step lifecycle`
        : '—';

    case 'aggregate':
      return 'aggregate_function' in def
        ? `${def.aggregate_function}(${def.aggregate_field}) per ${def.period}`
        : '—';

    case 'relational':
      return 'trigger_entity_type' in def
        ? `${def.trigger_entity_type} → ${def.required_action}`
        : '—';

    default:
      return '—';
  }
}

function fmt(v: unknown): string {
  if (typeof v === 'number') return v.toLocaleString();
  return String(v ?? '');
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Raw backend rule shape — a subset of the Pydantic RuleResponse.
 * Kept intentionally loose (Record<string, unknown>-compatible) so the
 * mapper is resilient to minor backend additions.
 */
export interface BackendRuleResponse {
  id: string;
  rule_name: string;
  entity_type: string | null;
  field_name: string | null;
  rule_type: string;
  rule_definition: Record<string, unknown>;
  severity: string;
  rejection_mode: string;
  description: string | null;
  is_active: boolean;
  version: number;
  rule_code: string | null;
  department: string | null;
  state: string | null;
  severity_level: string | null;
  applies_to_entity_types: string[] | null;
  created_by: string | null;
  source: string | null;
  /** Engine lane evaluating this rule's type; 'deferred'/'sweep' = not enforced yet (s95). */
  evaluation_lane?: string | null;
  // Present on RuleWithValidationResponse (create/update endpoints).
  validation_status?: ValidationStatus | null;
  validation_conflicts?: ValidationConflict[];
}

/** Pull the validation block out of a create/update response if present. */
export function extractValidation(r: BackendRuleResponse): RuleValidation | null {
  if (!r.validation_status) return null;
  return {
    status: r.validation_status,
    conflicts: r.validation_conflicts ?? [],
  };
}

/** Map a single backend rule to the frontend BusinessRule shape. */
export function mapRule(r: BackendRuleResponse): BusinessRule {
  let deptCode: string | null = null;
  if (r.department) {
    deptCode = DEPT_TO_CODE[r.department] ?? null;
    if (!deptCode) {
      console.warn(`Unknown department slug from backend: "${r.department}". Update DEPT_TO_CODE.`);
    }
  }

  return {
    id: r.id,
    ruleCode: r.rule_code ?? '—',
    name: r.rule_name,
    department: deptCode ?? 'UNASSIGNED',
    departmentSlug: r.department ?? null,
    ruleType: (RULE_TYPE_TO_SPEC[r.rule_type] ?? r.rule_type) as RuleType,
    state: (r.state ?? 'proposed') as RuleState,
    entityType: r.entity_type ?? '—',
    fieldName: r.field_name ?? null,
    threshold: buildThreshold(r.rule_type, r.rule_definition),
    ruleDefinition: r.rule_definition ?? {},
    severity: r.severity ?? 'soft_fail',
    rejectionMode: r.rejection_mode ?? 'hard',
    description: r.description ?? null,
    firesCount: 0, // No backend data for this yet — honest placeholder
    severityLevel: (r.severity_level ?? 'medium') as RuleSeverityLevel,
    source: (r.source ?? 'custom') as RuleSource,
    evaluationLane: r.evaluation_lane ?? null,
  };
}

/** Map a list of backend rules. */
export function mapRules(rules: BackendRuleResponse[]): BusinessRule[] {
  return rules.map(mapRule);
}
